import { useEffect, useRef, useState } from 'react'
import styles from './AddSkillModal.module.css'

/**
 * AddSkillModal — capture a new operator-defined skill (name + type) and save it.
 *
 * Pure render/collect component: the page owns the skill-types list and the save
 * call. Non-dismissable except via Cancel (no backdrop/Escape close) so an in-progress
 * entry isn't lost to a stray click, mirroring ProposedSkillDialog.
 *
 * Props:
 *   types {string[]}  — valid skill types for the dropdown (from the page)
 *   busy  {boolean}   — disables the form while the save call is inflight
 *   error {string}    — error text to show in-dialog (the save failed; stays open)
 *   onSave {function} — called with { skillName, skillType }
 *   onCancel {function}
 */
export default function AddSkillModal({ types = [], busy = false, error = '', onSave, onCancel }) {
  const [skillName, setSkillName] = useState('')
  // The page loads the types before opening the modal, so the list is already present
  // at mount — default the dropdown to the first type without a syncing effect.
  const [skillType, setSkillType] = useState(() => types[0] ?? '')
  const firstFieldRef = useRef(null)

  useEffect(() => {
    firstFieldRef.current?.focus()
  }, [])

  const canSave = skillName.trim() !== '' && skillType !== '' && !busy

  function handleSave() {
    if (!canSave) return
    onSave({ skillName: skillName.trim(), skillType })
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Add skill manually">
        <h2 className={styles.title}>Add Skill Manually</h2>
        <p className={styles.message}>
          Define a skill yourself. GaussIQ will begin tracking its coverage and
          proposing it for team members.
        </p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Skill Name</span>
          <input
            ref={firstFieldRef}
            className={styles.input}
            type="text"
            value={skillName}
            onChange={e => setSkillName(e.target.value)}
            placeholder="e.g. Salesforce Apex debugging"
            disabled={busy}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Skill Type</span>
          <select
            className={styles.select}
            value={skillType}
            onChange={e => setSkillType(e.target.value)}
            disabled={busy}
          >
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={!canSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
